import type { Table } from '@tanstack/react-table';
import { PaginationControls } from '@/components/ui/pagination-controls';

export interface DataTablePagerProps<TData> {
  table: Table<TData>;
  /** Total rows across all pages (filtered count in client mode, server rowCount in server mode). */
  totalItems: number;
  pageSizeOptions?: number[];
  showPageSizeSelector?: boolean;
  itemLabel?: string;
}

/**
 * Adapter that renders the canonical {@link PaginationControls} from a TanStack table
 * instance. This is the deliberate replacement for the grid's old inline native-select
 * pager — one pager across the whole app (ENT-1897, Option B).
 */
export function DataTablePager<TData>({
  table,
  totalItems,
  pageSizeOptions,
  showPageSizeSelector = true,
  itemLabel = 'rows',
}: DataTablePagerProps<TData>) {
  const { pageIndex, pageSize } = table.getState().pagination;
  const startIndex = pageIndex * pageSize;

  return (
    <PaginationControls
      currentPage={pageIndex + 1}
      totalPages={Math.max(1, table.getPageCount())}
      totalItems={totalItems}
      pageSize={pageSize}
      startIndex={startIndex}
      endIndex={Math.min(startIndex + pageSize, totalItems)}
      hasNextPage={table.getCanNextPage()}
      hasPreviousPage={table.getCanPreviousPage()}
      onNextPage={() => table.nextPage()}
      onPreviousPage={() => table.previousPage()}
      onGoToPage={(page) => table.setPageIndex(page - 1)}
      onPageSizeChange={(size) => table.setPageSize(size)}
      pageSizeOptions={pageSizeOptions}
      showPageSizeSelector={showPageSizeSelector}
      itemLabel={itemLabel}
    />
  );
}
